/**
 * Onboarding modal for choosing the audio source. Phase 1 surfaces three
 * paths:
 *
 *   1. Tab / system audio (getDisplayMedia) — most flexible, but requires
 *      the user to tick "Share tab audio" in the picker.
 *   2. Microphone — works in every browser, immediate, but quality varies.
 *   3. Demo loop — bundled procedural fallback, no permissions needed; the
 *      default for first-run aesthetic tuning.
 *
 * The modal renders once (lazy DOM creation), can be reopened via the `o`
 * hotkey, and surfaces capture errors inline so the user can re-pick
 * without reloading the page.
 */

export type AudioChoice = 'tab' | 'mic' | 'demo';

export type OnboardingHandlers = {
  onChoose: (choice: AudioChoice) => Promise<void>;
};

let modal: HTMLDivElement | null = null;
let errorBox: HTMLDivElement | null = null;
let buttons: HTMLButtonElement[] = [];
let currentHandlers: OnboardingHandlers | null = null;
let busy = false;

export function openOnboarding(
  handlers: OnboardingHandlers,
  initialError?: string,
): void {
  currentHandlers = handlers;
  if (!modal) buildModal();
  if (!modal) return;
  modal.classList.remove('hidden');
  if (initialError) showError(initialError);
  else clearError();
}

export function closeOnboarding(): void {
  modal?.classList.add('hidden');
  clearError();
}

function buildModal(): void {
  const root = document.createElement('div');
  root.id = 'onboarding';
  root.classList.add('hidden');

  const panel = document.createElement('div');
  panel.className = 'panel';
  panel.innerHTML = `
    <h1>Pick an audio source</h1>
    <p class="lead">
      The visualizer reads audio in real time and draws around it.
      You can change this any time with the <kbd>O</kbd> hotkey.
    </p>
  `;

  const choices: Array<{
    key: AudioChoice;
    title: string;
    desc: string;
  }> = [
    {
      key: 'tab',
      title: 'Tab or system audio',
      desc:
        'Open the picker, choose a tab playing music, then tick ' +
        '"Share tab audio". Best fidelity. Browser support varies by OS.',
    },
    {
      key: 'mic',
      title: 'Microphone',
      desc:
        'Use the device microphone — point it at speakers or a phone. ' +
        'Works everywhere; signal quality depends on the room.',
    },
    {
      key: 'demo',
      title: 'Bundled demo loop',
      desc:
        'A 110 BPM procedural synth loop. No permissions required. ' +
        'Use this for repeatable visual tuning.',
    },
  ];

  buttons = [];
  for (const c of choices) {
    const btn = document.createElement('button');
    btn.className = 'source';
    btn.type = 'button';
    btn.innerHTML = `<strong>${escapeHtml(c.title)}</strong><span class="desc">${escapeHtml(c.desc)}</span>`;
    btn.addEventListener('click', () => void handleClick(c.key));
    panel.appendChild(btn);
    buttons.push(btn);
  }

  const tip = document.createElement('div');
  tip.className = 'tip';
  tip.textContent =
    'Tip: in the Chrome / Edge picker, the "Share tab audio" checkbox ' +
    'lives at the bottom-left. Without it the visualizer will fall back to silence.';
  panel.appendChild(tip);

  errorBox = document.createElement('div');
  errorBox.className = 'err hidden';
  panel.appendChild(errorBox);

  const footer = document.createElement('div');
  footer.className = 'footer';
  footer.innerHTML =
    'Hotkeys after start: <kbd>F</kbd> fullscreen · ' +
    '<kbd>H</kbd> HUD · <kbd>←/→</kbd> presets · ' +
    '<kbd>Space</kbd> random · <kbd>O</kbd> change source · ' +
    '<kbd>T</kbd> test card';
  panel.appendChild(footer);

  root.appendChild(panel);
  document.body.appendChild(root);
  modal = root;
}

async function handleClick(choice: AudioChoice): Promise<void> {
  if (busy || !currentHandlers) return;
  busy = true;
  setEnabled(false);
  clearError();
  try {
    await currentHandlers.onChoose(choice);
    closeOnboarding();
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    showError(msg);
  } finally {
    setEnabled(true);
    busy = false;
  }
}

function setEnabled(enabled: boolean): void {
  for (const b of buttons) b.disabled = !enabled;
}

function showError(msg: string): void {
  if (!errorBox) return;
  errorBox.textContent = msg;
  errorBox.classList.remove('hidden');
}

function clearError(): void {
  if (!errorBox) return;
  errorBox.textContent = '';
  errorBox.classList.add('hidden');
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"]/g, (c) => {
    switch (c) {
      case '&': return '&amp;';
      case '<': return '&lt;';
      case '>': return '&gt;';
      case '"': return '&quot;';
      default: return c;
    }
  });
}
